import { useLocation } from 'react-router-dom'
import { usePageFreshness } from '../../hooks/useDiscordDocsFreshness'
import { getDocPageByPath, formatDiscordDate } from '@/data/discordDocsUrls'
import styles from './OutdatedBanner.module.css'

interface OutdatedBannerProps {
  language?: 'cs' | 'en'
}

export default function OutdatedBanner({ language = 'cs' }: OutdatedBannerProps) {
  const location = useLocation()
  const { pageData, loading, error } = usePageFreshness(location.pathname)
  const config = getDocPageByPath(location.pathname)

  if (config?.needsUpdate || pageData?.contentChanged) {
    return (
      <div className={styles.banner} role="status">
        <div className={styles.content}>
          <strong>
            {language === 'cs' ? 'Překlad vyžaduje aktualizaci' : 'Translation update needed'}
          </strong>
          <p>
            {language === 'cs'
              ? 'Originál se změnil a překlad vyžaduje nové porovnání. Historie popisuje poslední dokončenou kontrolu; aktuální znění najdete v originálu.'
              : 'The original has changed and this translation needs another review. The history describes the last completed comparison; use the original for the current text.'}
          </p>
          <a
            className={styles.link}
            href={config?.originalUrl || pageData?.originalUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {language === 'cs' ? 'Číst aktuální originál' : 'Read the current original'}
          </a>
        </div>
      </div>
    )
  }

  if (error || pageData?.error) {
    return (
      <p className={styles.banner} role="status">
        {language === 'cs'
          ? 'Aktuálnost originálu se nepodařilo ověřit. Zobrazená data odpovídají poslední dokončené kontrole.'
          : 'The original could not be checked. The dates shown reflect the last completed review.'}
      </p>
    )
  }

  if (loading || !pageData) {
    return null
  }

  // Don't show if page is not outdated
  if (!pageData.isOutdated) {
    return null
  }

  const formatDate = (dateStr: string) => formatDiscordDate(dateStr, language)

  return (
    <div className={styles.banner} role="status">
      <div className={styles.content}>
        <strong>
          {language === 'cs'
            ? 'Tento překlad může být zastaralý!'
            : 'This translation may be outdated!'}
        </strong>
        <p>
          {language === 'cs' ? (
            <>
              Oficiální stránka byla aktualizována{' '}
              <strong>{formatDate(pageData.originalDate!)}</strong>, ale náš překlad je z{' '}
              <strong>{formatDate(pageData.ourDate)}</strong>.
            </>
          ) : (
            <>
              The official page was updated on <strong>{formatDate(pageData.originalDate!)}</strong>
              , but our translation is from <strong>{formatDate(pageData.ourDate)}</strong>.
            </>
          )}
        </p>
        <a
          href={pageData.originalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {language === 'cs' ? 'Zobrazit aktuální verzi →' : 'View current version →'}
        </a>
      </div>
    </div>
  )
}
